<script>
import baseMixin from '@/mixins/baseMixin'
import dayjs from '@/services/date'
import DateFormatToken from '@/constants/date/DateFormatToken'
import { TimesheetOperationTypeInts } from '@/constants/disputes/TimesheetOperationType'

export default {
  name: 'DisputesTableBookingDate',
  mixins: [baseMixin],
  props: {
    dispute: {
      type: Object,
      required: true,
    },
  },
  computed: {
    DateFormatToken() {
      return DateFormatToken
    },
    getClientTimes() {
      if (!this.dispute.clientHasSubmitted) return null
      return this.convertTimeIntoDate(
        this.dispute.clientTimesheet.details.startTime,
        this.dispute.clientTimesheet.details.endTime,
        this.dispute.startDate
      )
    },
    getCandidateTimes() {
      if (!this.dispute.candidateHasSubmitted) return null
      return this.convertTimeIntoDate(
        this.dispute.candidateTimesheet.details.startTime,
        this.dispute.candidateTimesheet.details.endTime,
        this.dispute.startDate
      )
    },
    hasCandidateModifiedTimesheet() {
      if (!this.dispute.candidateHasSubmitted) return false
      return (
        this.dispute.candidateTimesheet.details.operationType ===
        TimesheetOperationTypeInts.MODIFY
      )
    },
    hasClientModifiedTimesheet() {
      if (!this.dispute.clientHasSubmitted) return false
      return (
        this.dispute.clientTimesheet.details.operationType ===
        TimesheetOperationTypeInts.MODIFY
      )
    },
    getLatestAndPreviousBookingTime() {
      const candidateSubmittedDate = this.hasCandidateModifiedTimesheet
        ? this.dispute.candidateTimesheet?.submittedDateUtc
        : null
      const clientSubmittedDate = this.hasClientModifiedTimesheet
        ? this.dispute.clientTimesheet?.submittedDateUtc
        : null

      let latestStartDate = this.dispute.startDate
      let latestEndDate = this.dispute.endDate

      let previousStartDate = null
      let previousEndDate = null

      if (candidateSubmittedDate && clientSubmittedDate) {
        const candidateTimesheetIsLatest = dayjs(
          candidateSubmittedDate
        ).isAfter(dayjs(clientSubmittedDate))

        latestStartDate = candidateTimesheetIsLatest
          ? this.getCandidateTimes.startDate
          : this.getClientTimes.startDate
        latestEndDate = candidateTimesheetIsLatest
          ? this.getCandidateTimes.endDate
          : this.getClientTimes.endDate

        previousStartDate = !candidateTimesheetIsLatest
          ? this.getCandidateTimes.startDate
          : this.getClientTimes.startDate
        previousEndDate = !candidateTimesheetIsLatest
          ? this.getCandidateTimes.endDate
          : this.getClientTimes.endDate
      } else if (candidateSubmittedDate) {
        latestStartDate = this.getCandidateTimes.startDate
        latestEndDate = this.getCandidateTimes.endDate

        previousStartDate = this.dispute.startDate
        previousEndDate = this.dispute.endDate
      } else if (clientSubmittedDate) {
        latestStartDate = this.getClientTimes.startDate
        latestEndDate = this.getClientTimes.endDate

        previousStartDate = this.dispute.startDate
        previousEndDate = this.dispute.endDate
      }

      return {
        latest: this.compileDateString(latestStartDate, latestEndDate),
        previous: previousStartDate
          ? this.compileDateString(previousStartDate, previousEndDate)
          : null,
      }
    },
    showPreviousBookingTime() {
      return !!this.getLatestAndPreviousBookingTime.previous
    },
  },
  methods: {
    formatDate(d, format = DateFormatToken.ll) {
      return dayjs(d).locale(this.$i18n.locale).format(format)
    },
    convertTimeIntoDate(startTime, endTime, bookingStartDate) {
      const startTimeObj = dayjs(startTime, DateFormatToken.HHmmss)
      const endTimeObj = dayjs(endTime, DateFormatToken.HHmmss)

      const startDate = dayjs(bookingStartDate)
        .set('hours', startTimeObj.hour())
        .set('minutes', startTimeObj.minute())
      const endDate = dayjs(bookingStartDate)
        .set('hours', endTimeObj.hour())
        .set('minutes', endTimeObj.minute())

      return { startDate, endDate }
    },
    compileDateString(startDate, endDate) {
      return `${this.formatDate(startDate)} - ${this.formatDate(
        startDate,
        DateFormatToken.LT
      )} - ${this.formatDate(endDate, DateFormatToken.LT)}`
    },
  },
}
</script>

<template>
  <div>
    <div>
      {{ getLatestAndPreviousBookingTime.latest }}
    </div>
    <div
      v-if="showPreviousBookingTime"
      class="text-decoration-line-through text-caption opacity-5"
    >
      {{ getLatestAndPreviousBookingTime.previous }}
    </div>
  </div>
</template>
